import { zodResolver } from "@hookform/resolvers/zod";
import {
    Modal,
    SelectField,
    TextAreaField,
    TextInputField,
    useToasts,
} from "@twinkltech/curriculum-architect-component-library";
import {
    ApiResponse,
    Curriculum,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { Button, ModalBody } from "@twinkltech/web-ui";
import React, { useEffect, useId, useState } from "react";
import { SubmitHandler, useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { useFeedback } from "@/providers/Feedback/Feedback.context";
import { captureAppError } from "@/utils/monitoring/sentry";

import { REQUIRED_FIELD_MESSAGE } from "@/utils/validation-messages";

import { GENERIC_CURRICULUM_FORM_NS } from "./GenericCurriculumModal.i18n";

export type RequiredGenericCurriculumModalProps = React.ComponentProps<
  typeof GenericCurriculumModal
>;

const CurriculumFormSchema = z.object({
  displayLabel: z.string().min(1, REQUIRED_FIELD_MESSAGE).max(120),
  description: z.string().max(500).optional(),
  status: z.enum(["active", "archived"]),
  progress: z.enum(["not_yet_planned", "planned", "planning_in_progress"]),
});

type CurriculumFormData = z.infer<typeof CurriculumFormSchema>;

export const GenericCurriculumModal = ({
  curriculum,
  error,
  loading = false,
  onClickClose,
  onPostSave,
  onSubmit,
  open,
  saveActionLabel,
  setOpen,
  title,
  ...props
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  saveActionLabel?: string;
  loading?: boolean;
  error?: Error | null;
  onClickClose?: () => void;
  onPostSave?: () => void;
  onSubmit: (data: Partial<Curriculum>) => ApiResponse<Partial<Curriculum>>;
  curriculum?: Curriculum | null;
} & Omit<
  React.ComponentProps<typeof Modal>,
  "open" | "onClose" | "children"
>) => {
  const { t } = useTranslation(GENERIC_CURRICULUM_FORM_NS);
  const { incrementEngagementCount } = useFeedback();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addErrorToast, addSuccessToast } = useToasts();

  // Accessibility IDs
  const formId = useId();
  const currNameInputId = useId();
  const currDescriptionInputId = useId();
  const currVisibilityInputId = useId();
  const currPlanningProgressInputId = useId();

  const formDefaultValues = React.useMemo<CurriculumFormData>(
    () => ({
      displayLabel: curriculum?.displayLabel ?? "",
      description: curriculum?.description ?? "",
      status: curriculum?.status ?? "active",
      progress: curriculum?.progress ?? "not_yet_planned",
    }),
    [
      curriculum?.displayLabel,
      curriculum?.description,
      curriculum?.status,
      curriculum?.progress,
    ]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<CurriculumFormData, unknown, CurriculumFormData>({
    resolver: zodResolver<CurriculumFormData, unknown, CurriculumFormData>(
      CurriculumFormSchema
    ),
    defaultValues: formDefaultValues,
  });

  // Note: Need to use a controller for the Headless UI custom components
  const { field: progressField } = useController({
    control,
    name: "progress",
  });
  const { field: statusField } = useController({
    control,
    name: "status",
  });

  // Set form values after loading and curriculum is available
  useEffect(() => {
    if (!loading && curriculum) {
      reset(formDefaultValues);
    }
  }, [loading, curriculum, reset, formDefaultValues]);

  const onHandleOnClose = () => {
    reset();
    setOpen(false);
  };

  const onFormSubmit: SubmitHandler<CurriculumFormData> = async (
    data,
    event
  ) => {
    event?.preventDefault();

    if (loading || isSubmitting) {
      return;
    }

    incrementEngagementCount();

    setIsSubmitting(true);
    const result = await onSubmit(data);

    if (result instanceof Error) {
      captureAppError(result, {
        action: "update",
        area: "curriculum",
        extra: {
          component: "GenericCurriculumModal",
          curriculumId: curriculum?.id || null,
        },
      });
      addErrorToast({
        message: "Update failed",
      });
    } else {
      addSuccessToast({
        message: "Curriculum updated successfully",
      });
      onHandleOnClose();
    }

    onPostSave?.();

    setIsSubmitting(false);
  };

  if (error) {
    return (
      <Modal
        hasCloseButton
        open={open}
        width="sm"
        onClose={() => setOpen(false)}
      >
        <ModalBody>Error: {error.message}</ModalBody>
      </Modal>
    );
  }

  if (!curriculum && curriculum !== null) {
    return (
      <Modal
        hasCloseButton
        open={open}
        width="sm"
        onClose={() => setOpen(false)}
      >
        <ModalBody>No curriculum data available</ModalBody>
      </Modal>
    );
  }

  return (
    <Modal
      hasCloseButton
      {...props}
      open={open}
      width="md"
      onClose={() => {
        onClickClose?.();
        onHandleOnClose();
      }}
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-2 max-h-[80vh]">
        <form
          className="py-2 gap-4 flex flex-col"
          id={formId}
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <TextInputField
            id={currNameInputId}
            infoText={t("inputs.name.info")}
            label={t("inputs.name.label")}
            maxLength={120}
            placeholder={t("inputs.name.placeholder")}
            {...register("displayLabel", { required: true })}
            errorText={errors.displayLabel?.message}
          />
          <TextAreaField
            id={currDescriptionInputId}
            infoText={t("inputs.description.info")}
            label={t("inputs.description.label")}
            maxLength={500}
            placeholder={t("inputs.description.placeholder")}
            {...register("description")}
            errorText={errors.description?.message}
          />
          <SelectField
            id={currVisibilityInputId}
            label={t("inputs.status.label")}
            options={[
              { value: "active", label: "Active" },
              { value: "archived", label: "Archived" },
            ]}
            {...statusField}
            defaultValue={formDefaultValues.status}
            errorText={errors.status?.message}
          />
          <SelectField
            id={currPlanningProgressInputId}
            label={t("inputs.progress.label")}
            options={[
              { value: "not_yet_planned", label: "Not yet planned" },
              { value: "planning_in_progress", label: "Planning in progress" },
              { value: "planned", label: "Planned" },
            ]}
            {...progressField}
            defaultValue={formDefaultValues.progress}
            errorText={errors.progress?.message}
          />
        </form>
      </Modal.Body>
      <Modal.Footer className="gap-2">
        <Button
          type="button"
          variant="outlined"
          onClick={() => {
            onClickClose?.();
            onHandleOnClose();
          }}
        >
          {t("actions.cancel", { ns: COMMON_NS })}
        </Button>
        <Button
          disabled={loading || !open || isSubmitting}
          form={formId}
          variant="primary"
        >
          {saveActionLabel || t("actions.save", { ns: COMMON_NS })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
